import React, { useEffect, useState, useRef } from 'react';
import { Upload, RefreshCw, Trash2 } from 'lucide-react';
import type { OnboardingStepProps } from '../onboardingSteps';
import { fetchHeroSection, createHeroSection, updateHeroSection, uploadImageFile } from '../onboardingApi';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Cropper } from '@/components/ui/image-crop';

const HeroOnboarding: React.FC<OnboardingStepProps> = ({ saveRef, onValidationChange, onPreviewChange, buildResult }) => {
  const [mainTitle, setMainTitle] = useState('');
  const [subtitle1, setSubtitle1] = useState('');
  const [subtitle2, setSubtitle2] = useState('');
  const [description, setDescription] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [heroId, setHeroId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState<'idle' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropPercent, setCropPercent] = useState<any>(null);
  const [imageError, setImageError] = useState('');
  const [prefilled, setPrefilled] = useState(false);
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const data = await fetchHeroSection();
        if (data && data._id) {
          setHeroId(data._id);
          setMainTitle(data.mainTitle || '');
          setSubtitle1(data.subtitle1 || '');
          setSubtitle2(data.subtitle2 || '');
          setDescription(data.description || '');
          setHeroImage(data.heroImage || buildResult?.channelImage || buildResult?.wikipediaImage || '');
          // If data was prefilled by the identity engine, flag it
          if (buildResult?.prefill?.heroCreated) {
            setPrefilled(true);
          }
        }
      } catch {
        setLoadError('Could not load your hero section. Please retry.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadAttempt]);

  useEffect(() => {
    onValidationChange(!loading && !loadError);
  }, [loading, loadError, onValidationChange]);

  useEffect(() => {
    if (loading || loadError) return;
    onPreviewChange?.({
      hero: { mainTitle, subtitle1, subtitle2, description, heroImage },
    });
  }, [loading, loadError, mainTitle, subtitle1, subtitle2, description, heroImage, onPreviewChange]);

  const performCropAndUpload = async (): Promise<string | null> => {
    if (!cropPercent || !selectedFile) return null;

    const originalUrl = URL.createObjectURL(selectedFile);
    const originalImg = new Image();

    await new Promise((resolve, reject) => {
      originalImg.onload = resolve;
      originalImg.onerror = reject;
      originalImg.src = originalUrl;
    });

    const imgWidth = originalImg.naturalWidth;
    const imgHeight = originalImg.naturalHeight;

    const cropX = (cropPercent.x / 100) * imgWidth;
    const cropY = (cropPercent.y / 100) * imgHeight;
    const cropWidth = (cropPercent.width / 100) * imgWidth;
    const cropHeight = (cropPercent.height / 100) * imgHeight;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = Math.round(cropWidth);
    canvas.height = Math.round(cropHeight);

    ctx?.drawImage(
      originalImg,
      Math.round(cropX),
      Math.round(cropY),
      Math.round(cropWidth),
      Math.round(cropHeight),
      0,
      0,
      Math.round(cropWidth),
      Math.round(cropHeight)
    );

    const mimeType = selectedFile.type || 'image/png';
    const croppedBlob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob || new Blob());
      }, mimeType, 1.0);
    });

    URL.revokeObjectURL(originalUrl);

    const url = await uploadImageFile(croppedBlob);
    return url;
  };

  useEffect(() => {
    saveRef.current = async () => {
      if (loading || loadError) throw new Error('Load the hero section before saving.');
      let imageUrl = heroImage;

      // Auto-upload if cropper is active with a pending crop
      if (uploadStep === 'cropping' && selectedFile && cropPercent) {
        const url = await performCropAndUpload();
        if (url) {
          imageUrl = url;
          setHeroImage(url);
          resetUploadState();
        }
      }

      const payload: Record<string, unknown> = {
        mainTitle: mainTitle.trim(),
        subtitle1: subtitle1.trim(),
        subtitle2: subtitle2.trim(),
        description: description.trim(),
      };
      if (imageUrl) {
        payload.heroImage = imageUrl;
      }

      if (heroId) {
        await updateHeroSection(heroId, payload);
      } else {
        await createHeroSection(payload);
      }
    };
  }, [saveRef, heroId, mainTitle, subtitle1, subtitle2, description, heroImage, uploadStep, selectedFile, cropPercent, loading, loadError]);

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    setCropPercent(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (heroImage) {
      fetch(heroImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'hero-image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          // Failed to load image for editing
        });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageError('');

    const validFormats = ['image/png', 'image/jpeg'];
    if (!validFormats.includes(file.type)) {
      setImageError('Only PNG and JPG formats are allowed.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setImageError('File size must be less than 2 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    setUploadStep('cropping');
  };

  const handleCropAndUpload = async () => {
    if (!cropPercent || !selectedFile) return;

    setUploading(true);
    try {
      const url = await performCropAndUpload();
      if (url) {
        setHeroImage(url);
        resetUploadState();
      }
    } catch {
      // Upload failed
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setHeroImage('');
    resetUploadState();
  };

  if (loading) {
    if (loadError) {
      return (
        <div role="alert" className="space-y-3 rounded-lg border border-border p-4">
          <p className="text-destructive">{loadError}</p>
          <Button type="button" variant="outline" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Retry loading hero</Button>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-9 bg-gray-100 rounded-lg" />
        <div className="h-9 bg-gray-100 rounded-lg" />
        <div className="h-9 bg-gray-100 rounded-lg" />
        <div className="h-20 bg-gray-100 rounded-lg" />
        <div className="h-44 w-full max-w-xs bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 onb-fade-in">
      {/* AI-enhanced indicator */}
      {prefilled && buildResult?.identity?.aiEnhanced && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 w-fit">
          <span>✨</span> AI-generated content. We encourage you to review and customize it to best reflect your story and style!
        </div>
      )}

      {/* Artist Name (mainTitle) */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label>
            Artist's Name
          </Label>
          {prefilled && mainTitle && (
            <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">AI-generated</span>
          )}
        </div>
        <Input
          value={mainTitle}
          onChange={(e) => setMainTitle(e.target.value)}
          placeholder="Artist Name or Band Name"
        />
        <p className="text-xs text-muted-foreground">
          Optional. Leave blank to hide the main heading.
        </p>
      </div>

      {/* Subtitle 1 – Define Profession */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label>
            Artist's Profession
          </Label>
          {/* <Label>
            Artist's Profession <span className="text-muted-foreground font-normal">(optional)</span>
          </Label> */}
          {prefilled && subtitle1 && (
            <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">AI-generated</span>
          )}
        </div>
        <Input
          value={subtitle1}
          onChange={(e) => setSubtitle1(e.target.value)}
          placeholder="Composer, Singer, Lyricist, etc."
        />
      </div>

      {/* Subtitle 2 – Define Artist */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label>
           Define Artist
          </Label>
          {prefilled && subtitle2 && (
            <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">AI-generated</span>
          )}
        </div>
        <Input
          value={subtitle2}
          onChange={(e) => setSubtitle2(e.target.value)}
          placeholder="Achievements, style, or a unique tagline"
        />
      </div>

      {/* Short Description */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label>
            Short Description
          </Label>
          {prefilled && description && (
            <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">AI-generated</span>
          )}
        </div>
        <Textarea
          value={description}
          onChange={(e) => {
            if (e.target.value.length <= 120) setDescription(e.target.value);
          }}
          placeholder="Enter artist's short description (line breaks will be preserved as paragraphs)"
          rows={3}
        />
        <p className="text-xs text-muted-foreground">
          {description.length}/120 characters
        </p>
      </div>

      {/* Hero Image */}
      <div className="space-y-1.5">
        <Label>
          Hero Image
        </Label>

        {uploadStep === 'idle' ? (
          !heroImage ? (
            <Card
              className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-5 sm:py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
              onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.currentTarget.classList.add('bg-muted/50');
              }}
              onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                e.currentTarget.classList.remove('bg-muted/50');
              }}
              onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.currentTarget.classList.remove('bg-muted/50');
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  const event = { target: { files } } as any;
                  handleFileChange(event);
                }
              }}
              onClick={openFileUpload}
            >
              <div className="grid space-y-3">
                <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                  <Upload className="size-5" />
                  <div>
                    Drop image here or{" "}
                    <Button
                      type="button"
                      variant="link"
                      className="text-primary hover:text-primary/80 p-0 h-auto font-normal"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFileUpload();
                      }}
                    >
                      browse
                    </Button>{" "}
                    to upload
                  </div>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
              <span className="text-xs text-muted-foreground mt-1 block">
                Supported: JPG, PNG (max 2 MB) | Aspect ratio: 1:1.25
              </span>
            </Card>
          ) : (
            <div className="space-y-2">
              <Card
                className="group flex max-h-[280px] sm:max-h-[400px] w-full flex-col items-center justify-center gap-4 py-5 sm:py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={openCropperForExisting}
              >
                <div
                  className="relative w-48 h-60 sm:w-56 sm:h-70 rounded overflow-hidden"
                  style={{
                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                    backgroundSize: '16px 16px',
                    backgroundPosition: '0 0, 8px 8px',
                    backgroundRepeat: 'repeat'
                  }}
                >
                  <img
                    src={heroImage}
                    alt="uploaded"
                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    title="Click to edit image"
                  />
                </div>
                <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                  <Upload className="size-4" />
                  <span>Click to edit or re-upload</span>
                </div>
              </Card>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={openFileUpload}
                >
                  <RefreshCw className="size-3.5" />
                  Change
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-danger"
                  onClick={removeImage}
                >
                  <Trash2 className="size-3.5" />
                  Remove
                </Button>
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
              />
            </div>
          )
        ) : uploadStep === 'cropping' && selectedFile ? (
          <div className="space-y-2">
            <Card className="flex flex-col items-center border border-border">
              <div className="w-full border-b border-border p-3">
                <p className="text-sm font-medium text-foreground text-center">Adjust crop area (1:1.25 Aspect) - Drag to move, edges to resize</p>
              </div>
              <div className="w-full flex justify-center py-3">
                <Cropper
                  file={selectedFile}
                  aspect={1 / 1.25}
                  maxImageSize={2 * 1024 * 1024}
                  keepSelection
                  onComplete={(_pixelCrop, percentCrop) => {
                    setCropPercent(percentCrop);
                  }}
                  onCrop={() => {}}
                  onCropRef={cropperRef}
                />
              </div>
            </Card>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={handleCropAndUpload}
                disabled={uploading}
                size="sm"
                loading={uploading}
                loadingText="Uploading..."
              >
                {heroImage ? 'Update' : 'Upload'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetUploadState}
                disabled={uploading}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        {imageError && (
          <p className="text-sm text-red-500 mt-1">{imageError}</p>
        )}
      </div>
    </div>
  );
};

export default HeroOnboarding;
